import { useLayoutEffect, useRef } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs, { type Dayjs } from 'dayjs';
import {
    useGridApiContext,
    useGridSelector,
    gridEditRowsStateSelector,
    type GridRenderEditCellParams
} from '../lib/dataGrid';
import { completedTimeOnBlur, type TimeFieldRef } from './timeFieldAutoMinutes';
import { latestBookableTime } from '../features/timeTracking/timeMath';

/**
 * The stored field is an 'HH:mm' string; anchor it to an arbitrary date so dayjs
 * can parse/format just the time of day. Every bound below is anchored the same
 * way, so they compare as times rather than as dates.
 */
function parseTime(value: string): Dayjs | null {
    if (!value) {
        return null;
    }
    const parsed = dayjs(`2000-01-01T${value}`);
    return parsed.isValid() ? parsed : null;
}

/** The earliest of the given bounds, or undefined when there are none. */
function earliest(times: (Dayjs | null)[]): Dayjs | undefined {
    const known = times.filter((time): time is Dayjs => time !== null);
    if (known.length === 0) {
        return undefined;
    }
    return known.reduce((soFar, candidate) =>
        soFar.isBefore(candidate) ? soFar : candidate
    );
}

/**
 * Grid edit cell backed by the MUI X TimePicker (24h). The underlying field is an
 * 'HH:mm' string; the clock picks a value and the text field still accepts typed
 * input. Styled borderless so it fills the cell like the grid's native edit
 * input, and surfaces the validation error as a tooltip — mirrors GridDateEditCell.
 *
 * `afterField`/`beforeField` name the row's other time field, so the pair bounds
 * itself: picking a start greys out ends at or before it, and vice versa.
 *
 * Typing just the hour and tabbing on fills the minutes with 00 — see
 * completedTimeOnBlur.
 */
export default function GridTimeEditCell(
    props: GridRenderEditCellParams & {
        disableFuture?: boolean;
        /** Field whose time this cell must come strictly AFTER (e.g. startTime). */
        afterField?: string;
        /** Field whose time this cell must come strictly BEFORE (e.g. endTime). */
        beforeField?: string;
    }
) {
    const {
        id,
        field,
        value,
        error,
        hasFocus,
        row,
        disableFuture,
        afterField,
        beforeField
    } = props as GridRenderEditCellParams & {
        error?: string | null;
        disableFuture?: boolean;
        afterField?: string;
        beforeField?: string;
    };
    const apiRef = useGridApiContext();
    const inputRef = useRef<HTMLInputElement>(null);
    // The handle on to what is typed in each section — the only way to see an
    // hour that has no minutes yet (see completedTimeOnBlur).
    const fieldRef = useRef<TimeFieldRef>(null);

    useLayoutEffect(() => {
        if (hasFocus) {
            inputRef.current?.focus();
        }
    }, [hasFocus]);

    // The sibling's IN-PROGRESS value: in row edit mode both cells are open at
    // once, so its committed value on `row` is stale the moment it is touched.
    // Read through a selector so this cell re-renders when the sibling changes.
    const editState = useGridSelector(apiRef, gridEditRowsStateSelector);
    function siblingTime(siblingField: string | undefined): Dayjs | null {
        if (!siblingField) {
            return null;
        }
        const edited = editState[id]?.[siblingField]?.value;
        const raw =
            edited !== undefined
                ? edited
                : (row as Record<string, unknown>)[siblingField];
        return parseTime(`${raw ?? ''}`);
    }

    // On today's workday, disable times beyond the bookable bound — an entry may
    // run up to an hour ahead of the clock, not further (the date part of
    // maxTime is ignored by the picker).
    const rowDate = (row as { date?: string })?.date;
    const bookableUntil = latestBookableTime();
    const nowBound =
        disableFuture &&
        rowDate === dayjs().format('YYYY-MM-DD') &&
        bookableUntil
            ? parseTime(bookableUntil)
            : null;

    // Strict bounds: a span can't start and end in the same minute, so step one
    // minute past the sibling (MUI's minTime/maxTime are inclusive).
    const after = siblingTime(afterField);
    const before = siblingTime(beforeField);
    const minTime = after ? after.add(1, 'minute') : undefined;
    const maxTime = earliest([
        nowBound,
        before ? before.subtract(1, 'minute') : null
    ]);

    function handleChange(next: Dayjs | null) {
        const formatted = next && next.isValid() ? next.format('HH:mm') : '';
        apiRef.current.setEditCellValue({ id, field, value: formatted });
    }

    // Leaving the cell with only an hour typed commits the full hour. Blur is
    // the moment the grid moves on (Tab to the next field, or out of the row),
    // and MUI does not raise it while focus is merely crossing from the hour
    // section to the minute one — so this can never pre-empt the minutes.
    function handleBlur() {
        const completed = completedTimeOnBlur(fieldRef.current);
        if (completed) {
            apiRef.current.setEditCellValue({ id, field, value: completed });
        }
    }

    return (
        <Tooltip
            open={!!error && hasFocus}
            title={error ?? ''}
            arrow
            placement="bottom"
        >
            <span style={{ display: 'flex', width: '100%', height: '100%' }}>
                <TimePicker
                    ampm={false}
                    minTime={minTime}
                    maxTime={maxTime}
                    value={parseTime(value as string)}
                    onChange={handleChange}
                    slotProps={{
                        field: { fieldRef },
                        textField: {
                            inputRef,
                            fullWidth: true,
                            variant: 'standard',
                            onBlur: handleBlur,
                            error: !!error,
                            sx: {
                                height: '100%',
                                '& .MuiInputBase-root, & .MuiPickersInputBase-root':
                                    {
                                        height: '100%',
                                        fontSize: 'inherit',
                                        paddingLeft: '9px'
                                    },
                                '& .MuiInputBase-root::before, & .MuiInputBase-root::after, & .MuiPickersInputBase-root::before, & .MuiPickersInputBase-root::after':
                                    {
                                        borderBottom: 'none !important'
                                    }
                            }
                        }
                    }}
                />
            </span>
        </Tooltip>
    );
}
